import { AxiosError, AxiosRequestConfig } from 'axios'
import type { Fetcher } from 'graphql-ts-client-api'
import { AppProfile } from './app-profile'
import { fetchDelay } from '../base/helpers'
import { IServiceOptions } from '../base/types'
import { ApiAlertContext } from '../base/helpers'
import { createHttpService } from '../base/gateway'
import type { IGraphqlQueryOptions, IGraphqlRequestOptions } from './types'
import ServiceBase from '../base/service-base'

export interface IGraphqlRequestParams {
  baseUrl: string
  execute: (...p: any[]) => Promise<any>
  options?: IGraphqlRequestOptions
  serviceOptions?: IServiceOptions
}

class GraphqlRequest extends ServiceBase {
  private _execute: (...p: any[]) => Promise<any>
  private _options: IGraphqlRequestOptions
  /**
   * Constructor for GraphqlRequest.
   * @param url The GraphQL endpoint URL.
   * @param executor Custom executor function for GraphQL requests.
   * @param options Additional options for the request.
   */
  constructor(params: IGraphqlRequestParams) {
    super(createHttpService(params.baseUrl), params.serviceOptions)
    // const { autoDetectSite, site } = options ?? {}
    this._options = params.options ?? {}

    this._execute = params.execute

    // Initialize autoDetectSite and site getter
    this.getSite = this._getSite
    if (params.options?.site && typeof params.options.site === 'function') {
      this.getSite = params.options.site
    } else {
      this.site = params.options?.site as string | undefined
    }
  }
  private site?: string
  /**
   * Adds the site to the request configuration headers.
   * @param config Optional AxiosRequestConfig to modify.
   * @param ignoreSite If true, skips adding the site header.
   * @returns Modified AxiosRequestConfig with the site header.
   */
  addSite(config?: AxiosRequestConfig, ignoreSite?: boolean) {
    if (this.site && config?.headers && !ignoreSite) {
      config.headers['graphql-site'] = this.site
    }
    return config ?? {}
  }
  /**
   * Sets the current site for GraphQL requests.
   * @param site The site string to set.
   */
  setSite = (site: string) => {
    this.site = site
  }
  /**
   * Default executor for GraphQL requests using POST.
   * @param request GraphQL query string.
   * @param variables Variables for the query.
   * @param signal Optional AbortSignal for cancellation.
   * @param ignoreSite If true, skips adding the site header.
   * @returns Promise with the server response.
   */
  private setGraphQLExecutor = async (request: string, variables: object, signal?: AbortSignal, ignoreSite?: boolean) => {
    const config = { signal }
    const finalConfig = await this.addCustomHeader(config, ignoreSite)
    return this.post('', { query: request, variables }, finalConfig)
  }
  /**
   * Default implementation to get the current site.
   * Stores the site in global.window.wwfd.GraphqlSite for reuse.
   * @returns Promise resolving to the current site string.
   */
  getSite: () => Promise<string>
  private _getSite = async () => {
    if (typeof global.window === 'undefined') {
      return await AppProfile.getGraphqlSite()
    }
    const windowTemp = global.window as any
    if (typeof windowTemp['wwfd'] !== 'object') {
      windowTemp['wwfd'] = {}
    }
    if (!windowTemp['wwfd'].GraphqlSite) {
      windowTemp['wwfd'].GraphqlSite = await AppProfile.getGraphqlSite()
    }
    return windowTemp['wwfd'].GraphqlSite
  }
  /**
   * Automatically detects the site by calling getSite.
   * Sets the detected site using setSite.
   */
  private autoDetectSite = async () => {
    const site = await this.getSite()
    this.setSite(site)
  }
  /**
   * Adds a custom header to the request configuration.
   * @param config Optional AxiosRequestConfig to modify.
   * @param ignoreSite If true, skips adding the site header.
   * @returns Modified AxiosRequestConfig with the custom header.
   */
  async addCustomHeader(config?: AxiosRequestConfig, ignoreSite?: boolean) {
    const cfg = await super.addCustomHeader(config)
    return this.addSite(cfg, ignoreSite)
  }
  /**
   * Gets the current site for GraphQL requests.
   * @returns The current site string.
   */
  private getParamExecutor = (...p: any[]): Parameters<typeof this.setGraphQLExecutor> => {
    const [request, variables, signal, ignoreSite] = p
    if (typeof request !== 'string' || request.length === 0) {
      throw new Error('Request must be a non-empty string')
    }
    if (typeof variables !== 'object') {
      throw new Error('Variables must be an object')
    }
    if (signal && !(signal instanceof AbortSignal)) {
      throw new Error('Signal must be an instance of AbortSignal')
    }
    return [request, variables, signal, ignoreSite]
  }

  /**
   * Executes a GraphQL query or mutation with the provided fetcher.
   * Automatically detects the site if autoDetectSite is true.
   * @param fetcher The fetcher function for the query or mutation.
   * @param options Optional IGraphqlQueryOptions for the request.
   * @returns Promise with the result of the query or mutation.
   */
  async query<TData extends object, TVariables extends object>(
    fetcher: Fetcher<'Query' | 'Mutation', TData, TVariables>,
    options?: IGraphqlQueryOptions
  ): Promise<TData> {
    let autoDetectSite = this._options.autoDetectSite
    let ignoreSite = this._options.ignoreSite

    if (options?.autoDetectSite !== undefined) {
      autoDetectSite = options.autoDetectSite
    }
    if (options?.ignoreSite !== undefined) {
      ignoreSite = options.ignoreSite
    }

    if (!ignoreSite && autoDetectSite !== false) {
      await this.autoDetectSite()
    } else if (!ignoreSite && options?.site) {
      this.setSite(options.site ?? (this._options.site as string))
    }

    const res = await this._execute(fetcher, {
      executor: (...params: any[]) => {
        // Ensure params always has signal and ignoreSite in correct positions
        const [request, variables] = params
        const signal = options?.signal
        return fetchDelay(() => this.setGraphQLExecutor(...this.getParamExecutor(request, variables, signal, ignoreSite)), options?.delay ?? 500)
      },
      ...(options ? options : {})
    })
    return res
  }
  /**
   * Executes a GraphQL query or mutation and handles errors by pushing them to ApiAlertContext.
   * @param fetcher The fetcher function for the query or mutation.
   * @param options Optional IGraphqlQueryOptions for the request.
   * @returns Promise with the result of the query or mutation.
   */
  async queryPushNotify<TData extends object, TVariables extends object>(
    fetcher: Fetcher<'Query' | 'Mutation', TData, TVariables>,
    options?: IGraphqlQueryOptions
  ) {
    try {
      return await this.query(fetcher, options)
    } catch (err) {
      const error = err as AxiosError
      const data = error.response?.data as any
      if (data) {
        const keys = Object.keys(data)
        keys.forEach((key) => {
          const errors = data[key] as any
          if (Array.isArray(errors)) {
            errors.forEach((message) => {
              ApiAlertContext?.ApiAlert?.PushError(message)
            })
          } else if (typeof errors.message === 'string') {
            ApiAlertContext?.ApiAlert?.PushError(errors.message)
          }
        })
      }
      throw error
    }
  }
}
export default GraphqlRequest
/*
 * Factory function to create a new GraphqlRequest instance.
 * @param args Arguments to pass to the GraphqlRequest constructor.
 * @returns A new instance of GraphqlRequest.
 */
export const createGraphqlRequest = (params: IGraphqlRequestParams) => {
  return new GraphqlRequest({ ...params })
}
